import { normalizeUFOVLanguage } from './ufovLocales';

// One concise, mechanic-aware instruction is preferable to falling back to an
// English three-step tutorial. The four games whose rules cannot be inferred
// safely from their category have explicit overrides in every locale.
const TUTORIAL_COPY = {
    en: {
        memory: 'Study the items, remember what appeared, then choose the matching answer.',
        attention: 'Focus on the target, ignore distractions, and respond carefully.',
        speed: 'Find the targets in order as quickly and accurately as you can.',
        logic: 'Identify the rule, then choose the answer that follows it.',
        numbers: 'Study the numbers or quantities and choose the correct result.',
        spatial: 'Follow the shape or route in your mind, then choose the correct result.',
        audio: 'Listen carefully, remember the order, then repeat it.',
        planning: 'Plan your moves and reach the goal in as few steps as possible.',
        coordination: 'Use both controls and keep both markers inside their lanes.',
        'symbol-n-back': 'Compare each symbol with the earlier symbol indicated by your level, then choose yes or no.',
        'stroop-colors': 'Read the word, ignore its ink color, and tap the color named by the word.',
        'go-no-go': 'Press only when an Ace or King appears; do nothing for every other card.',
        'trail-making': 'Start at 1, then alternate number and letter without losing the path.',
    },
    fr: {
        memory: 'Observez, mémorisez les éléments, puis choisissez la bonne réponse.',
        attention: 'Concentrez-vous sur la cible, ignorez les distractions et répondez avec précision.',
        speed: 'Trouvez les cibles dans l’ordre aussi vite et précisément que possible.',
        logic: 'Repérez la règle, puis choisissez la réponse qui la respecte.',
        numbers: 'Examinez les nombres ou les quantités, puis choisissez le bon résultat.',
        spatial: 'Suivez mentalement la forme ou le trajet, puis choisissez le bon résultat.',
        audio: 'Écoutez attentivement, mémorisez l’ordre, puis reproduisez-le.',
        planning: 'Planifiez vos mouvements et atteignez l’objectif en un minimum d’étapes.',
        coordination: 'Utilisez les deux commandes et gardez les deux repères dans leur voie.',
        'symbol-n-back': 'Comparez chaque symbole au symbole précédent indiqué par votre niveau, puis choisissez oui ou non.',
        'stroop-colors': 'Lisez le mot, ignorez la couleur de l’encre et touchez la couleur nommée.',
        'go-no-go': 'Appuyez seulement pour un As ou un Roi ; ne faites rien pour les autres cartes.',
        'trail-making': 'Commencez à 1, puis alternez chiffre et lettre sans perdre le chemin.',
    },
    es: {
        memory: 'Observa, recuerda los elementos y elige la respuesta que coincida.',
        attention: 'Concéntrate en el objetivo, ignora las distracciones y responde con precisión.',
        speed: 'Encuentra los objetivos en orden con la mayor rapidez y precisión posible.',
        logic: 'Identifica la regla y elige la respuesta que la cumple.',
        numbers: 'Observa los números o cantidades y elige el resultado correcto.',
        spatial: 'Sigue mentalmente la forma o ruta y elige el resultado correcto.',
        audio: 'Escucha con atención, recuerda el orden y repítelo.',
        planning: 'Planifica los movimientos y alcanza el objetivo en el menor número de pasos.',
        coordination: 'Usa ambos controles y mantén los dos marcadores dentro de sus carriles.',
        'symbol-n-back': 'Compara cada símbolo con el símbolo anterior que indica tu nivel y elige sí o no.',
        'stroop-colors': 'Lee la palabra, ignora el color de la tinta y toca el color nombrado.',
        'go-no-go': 'Pulsa solo cuando aparezca un As o un Rey; no hagas nada con las demás cartas.',
        'trail-making': 'Empieza en 1 y alterna número y letra sin perder el recorrido.',
    },
    pt: {
        memory: 'Observe, memorize os itens e escolha a resposta correspondente.',
        attention: 'Concentre-se no alvo, ignore as distrações e responda com precisão.',
        speed: 'Encontre os alvos em ordem com a maior rapidez e precisão possível.',
        logic: 'Identifique a regra e escolha a resposta que a segue.',
        numbers: 'Observe os números ou quantidades e escolha o resultado correto.',
        spatial: 'Siga mentalmente a forma ou rota e escolha o resultado correto.',
        audio: 'Ouça com atenção, memorize a ordem e repita-a.',
        planning: 'Planeje os movimentos e alcance o objetivo com o menor número de etapas.',
        coordination: 'Use os dois controles e mantenha os marcadores dentro das faixas.',
        'symbol-n-back': 'Compare cada símbolo com o símbolo anterior indicado pelo seu nível e escolha sim ou não.',
        'stroop-colors': 'Leia a palavra, ignore a cor da tinta e toque na cor escrita.',
        'go-no-go': 'Toque apenas quando aparecer um Ás ou Rei; não faça nada nas outras cartas.',
        'trail-making': 'Comece no 1 e alterne número e letra sem perder o caminho.',
    },
    it: {
        memory: 'Osserva, memorizza gli elementi e scegli la risposta corrispondente.',
        attention: 'Concentrati sul bersaglio, ignora le distrazioni e rispondi con precisione.',
        speed: 'Trova i bersagli in ordine il più rapidamente e precisamente possibile.',
        logic: 'Individua la regola e scegli la risposta che la rispetta.',
        numbers: 'Osserva numeri o quantità e scegli il risultato corretto.',
        spatial: 'Segui mentalmente la forma o il percorso e scegli il risultato corretto.',
        audio: 'Ascolta attentamente, ricorda l’ordine e ripetilo.',
        planning: 'Pianifica le mosse e raggiungi l’obiettivo con il minor numero di passaggi.',
        coordination: 'Usa entrambi i controlli e mantieni i due indicatori nelle corsie.',
        'symbol-n-back': 'Confronta ogni simbolo con il simbolo precedente indicato dal tuo livello, poi scegli sì o no.',
        'stroop-colors': 'Leggi la parola, ignora il colore dell’inchiostro e tocca il colore scritto.',
        'go-no-go': 'Premi solo quando appare un Asso o un Re; non fare nulla con le altre carte.',
        'trail-making': 'Parti da 1 e alterna numero e lettera senza perdere il percorso.',
    },
    de: {
        memory: 'Präge dir die Elemente ein und wähle danach die passende Antwort.',
        attention: 'Konzentriere dich auf das Ziel, ignoriere Ablenkungen und antworte genau.',
        speed: 'Finde die Ziele möglichst schnell und genau in der richtigen Reihenfolge.',
        logic: 'Erkenne die Regel und wähle die Antwort, die ihr folgt.',
        numbers: 'Prüfe Zahlen oder Mengen und wähle das richtige Ergebnis.',
        spatial: 'Verfolge Form oder Weg im Kopf und wähle das richtige Ergebnis.',
        audio: 'Höre genau zu, merke dir die Reihenfolge und wiederhole sie.',
        planning: 'Plane deine Züge und erreiche das Ziel mit möglichst wenigen Schritten.',
        coordination: 'Nutze beide Steuerungen und halte beide Marker in ihren Spuren.',
        'symbol-n-back': 'Vergleiche jedes Symbol mit dem früheren Symbol, das dein Level vorgibt, und wähle Ja oder Nein.',
        'stroop-colors': 'Lies das Wort, ignoriere die Schriftfarbe und tippe auf die genannte Farbe.',
        'go-no-go': 'Drücke nur bei Ass oder König; bei allen anderen Karten tust du nichts.',
        'trail-making': 'Beginne bei 1 und wechsle zwischen Zahl und Buchstabe, ohne den Pfad zu verlieren.',
    },
    ja: {
        memory: '表示された項目をよく見て覚え、一致する答えを選びます。',
        attention: '目標に集中し、周囲の妨害を無視して正確に答えます。',
        speed: '目標を正しい順番で、できるだけ速く正確に見つけます。',
        logic: 'ルールを見つけ、そのルールに合う答えを選びます。',
        numbers: '数字や数量を確認し、正しい結果を選びます。',
        spatial: '形や経路を頭の中でたどり、正しい結果を選びます。',
        audio: '音をよく聞き、順番を覚えて同じ順に答えます。',
        planning: '手順を計画し、できるだけ少ない手数で目標を達成します。',
        coordination: '2つの操作を使い、両方のマーカーをレーン内に保ちます。',
        'symbol-n-back': '各記号を、レベルで指定された回数前の記号と比べ、「はい」か「いいえ」を選びます。',
        'stroop-colors': '文字を読み、文字色は無視して、書かれている色を選びます。',
        'go-no-go': 'AまたはKのカードだけ押し、それ以外のカードでは何もしません。',
        'trail-making': '1から始め、数字と文字を交互に順番どおりつなぎます。',
    },
    ko: {
        memory: '나타난 항목을 잘 보고 기억한 뒤 일치하는 답을 고르세요.',
        attention: '목표에 집중하고 방해 요소를 무시하며 정확히 답하세요.',
        speed: '목표를 올바른 순서로 최대한 빠르고 정확하게 찾으세요.',
        logic: '규칙을 파악하고 그 규칙에 맞는 답을 고르세요.',
        numbers: '숫자나 수량을 살펴보고 올바른 결과를 고르세요.',
        spatial: '도형이나 경로를 머릿속으로 따라가 올바른 결과를 고르세요.',
        audio: '소리를 주의 깊게 듣고 순서를 기억한 뒤 그대로 반복하세요.',
        planning: '움직임을 계획해 가능한 적은 단계로 목표에 도달하세요.',
        coordination: '두 조작기를 사용해 두 마커를 각 레인 안에 유지하세요.',
        'symbol-n-back': '각 기호를 레벨에 따라 지정된 이전 기호와 비교한 뒤 예 또는 아니요를 고르세요.',
        'stroop-colors': '단어를 읽고 글자색은 무시한 뒤 단어가 뜻하는 색을 누르세요.',
        'go-no-go': 'A나 K 카드가 나올 때만 누르고 다른 카드에는 아무것도 하지 마세요.',
        'trail-making': '1에서 시작해 숫자와 글자를 번갈아 순서대로 연결하세요.',
    },
    ar: {
        memory: 'راقب العناصر وتذكّر ما ظهر، ثم اختر الإجابة المطابقة.',
        attention: 'ركّز على الهدف وتجاهل المشتتات وأجب بدقة.',
        speed: 'اعثر على الأهداف بالترتيب بأسرع ما يمكنك وبدقة.',
        logic: 'اكتشف القاعدة ثم اختر الإجابة التي تتبعها.',
        numbers: 'افحص الأرقام أو الكميات واختر النتيجة الصحيحة.',
        spatial: 'تتبّع الشكل أو المسار ذهنيًا ثم اختر النتيجة الصحيحة.',
        audio: 'استمع جيدًا وتذكّر الترتيب ثم كرره.',
        planning: 'خطط لحركاتك وحقق الهدف بأقل عدد ممكن من الخطوات.',
        coordination: 'استخدم أداتي التحكم وأبقِ العلامتين داخل مساريهما.',
        'symbol-n-back': 'قارن كل رمز بالرمز السابق الذي يحدده مستواك، ثم اختر نعم أو لا.',
        'stroop-colors': 'اقرأ الكلمة وتجاهل لون الحبر، ثم اضغط اللون المذكور في الكلمة.',
        'go-no-go': 'اضغط فقط عند ظهور الآس أو الملك، ولا تفعل شيئًا مع البطاقات الأخرى.',
        'trail-making': 'ابدأ بالرقم 1 ثم تناوب بين رقم وحرف من دون فقدان المسار.',
    },
    sv: {
        memory: 'Studera föremålen, minns vad som visades och välj sedan rätt svar.',
        attention: 'Fokusera på målet, ignorera distraktioner och svara noggrant.',
        speed: 'Hitta målen i rätt ordning så snabbt och exakt som möjligt.',
        logic: 'Identifiera regeln och välj svaret som följer den.',
        numbers: 'Studera siffrorna eller mängderna och välj rätt resultat.',
        spatial: 'Följ formen eller vägen i huvudet och välj rätt resultat.',
        audio: 'Lyssna noga, minns ordningen och upprepa den.',
        planning: 'Planera dragen och nå målet med så få steg som möjligt.',
        coordination: 'Använd båda kontrollerna och håll markörerna i sina banor.',
        'symbol-n-back': 'Jämför varje symbol med den tidigare symbol som nivån anger och välj ja eller nej.',
        'stroop-colors': 'Läs ordet, ignorera textfärgen och tryck på färgen som ordet anger.',
        'go-no-go': 'Tryck bara när ett ess eller en kung visas; gör inget för övriga kort.',
        'trail-making': 'Börja på 1 och växla mellan siffra och bokstav utan att tappa vägen.',
    },
    no: {
        memory: 'Studer elementene, husk hva som ble vist, og velg riktig svar.',
        attention: 'Fokuser på målet, ignorer distraksjoner og svar nøyaktig.',
        speed: 'Finn målene i riktig rekkefølge så raskt og nøyaktig som mulig.',
        logic: 'Finn regelen og velg svaret som følger den.',
        numbers: 'Se på tallene eller mengdene og velg riktig resultat.',
        spatial: 'Følg formen eller ruten i hodet og velg riktig resultat.',
        audio: 'Lytt nøye, husk rekkefølgen og gjenta den.',
        planning: 'Planlegg trekkene og nå målet med færrest mulig steg.',
        coordination: 'Bruk begge kontrollene og hold markørene i hver sin bane.',
        'symbol-n-back': 'Sammenlign hvert symbol med det tidligere symbolet nivået angir, og velg ja eller nei.',
        'stroop-colors': 'Les ordet, ignorer tekstfargen og trykk på fargen ordet angir.',
        'go-no-go': 'Trykk bare når et ess eller en konge vises; gjør ingenting for andre kort.',
        'trail-making': 'Start på 1 og veksle mellom tall og bokstav uten å miste sporet.',
    },
    da: {
        memory: 'Se på elementerne, husk hvad der blev vist, og vælg det rigtige svar.',
        attention: 'Fokuser på målet, ignorér forstyrrelser, og svar præcist.',
        speed: 'Find målene i den rigtige rækkefølge så hurtigt og præcist som muligt.',
        logic: 'Find reglen, og vælg det svar der følger den.',
        numbers: 'Se på tallene eller mængderne, og vælg det rigtige resultat.',
        spatial: 'Følg formen eller ruten i tankerne, og vælg det rigtige resultat.',
        audio: 'Lyt omhyggeligt, husk rækkefølgen, og gentag den.',
        planning: 'Planlæg trækkene, og nå målet med så få trin som muligt.',
        coordination: 'Brug begge kontroller, og hold markørerne i hver sin bane.',
        'symbol-n-back': 'Sammenlign hvert symbol med det tidligere symbol, som niveauet angiver, og vælg ja eller nej.',
        'stroop-colors': 'Læs ordet, ignorér tekstfarven, og tryk på den farve ordet angiver.',
        'go-no-go': 'Tryk kun når et es eller en konge vises; gør intet ved andre kort.',
        'trail-making': 'Start ved 1, og skift mellem tal og bogstav uden at miste sporet.',
    },
    nl: {
        memory: 'Bekijk de items, onthoud wat verscheen en kies daarna het passende antwoord.',
        attention: 'Concentreer je op het doel, negeer afleiding en antwoord nauwkeurig.',
        speed: 'Vind de doelen in de juiste volgorde, zo snel en nauwkeurig mogelijk.',
        logic: 'Ontdek de regel en kies het antwoord dat die regel volgt.',
        numbers: 'Bekijk de getallen of hoeveelheden en kies het juiste resultaat.',
        spatial: 'Volg de vorm of route in gedachten en kies het juiste resultaat.',
        audio: 'Luister goed, onthoud de volgorde en herhaal die.',
        planning: 'Plan je zetten en bereik het doel in zo weinig mogelijk stappen.',
        coordination: 'Gebruik beide besturingen en houd beide markeringen in hun baan.',
        'symbol-n-back': 'Vergelijk elk symbool met het eerdere symbool dat je niveau aangeeft en kies ja of nee.',
        'stroop-colors': 'Lees het woord, negeer de inktkleur en tik op de genoemde kleur.',
        'go-no-go': 'Druk alleen bij een aas of heer; doe bij alle andere kaarten niets.',
        'trail-making': 'Begin bij 1 en wissel cijfer en letter af zonder het spoor te verliezen.',
    },
    pl: {
        memory: 'Przyjrzyj się elementom, zapamiętaj je i wybierz pasującą odpowiedź.',
        attention: 'Skup się na celu, ignoruj rozpraszacze i odpowiadaj dokładnie.',
        speed: 'Znajdź cele we właściwej kolejności tak szybko i dokładnie, jak potrafisz.',
        logic: 'Rozpoznaj regułę i wybierz odpowiedź, która jej odpowiada.',
        numbers: 'Sprawdź liczby lub ilości i wybierz prawidłowy wynik.',
        spatial: 'Prześledź w myślach kształt lub trasę i wybierz prawidłowy wynik.',
        audio: 'Słuchaj uważnie, zapamiętaj kolejność i ją powtórz.',
        planning: 'Zaplanuj ruchy i osiągnij cel w jak najmniejszej liczbie kroków.',
        coordination: 'Używaj obu elementów sterujących i utrzymuj znaczniki na torach.',
        'symbol-n-back': 'Porównaj każdy symbol z wcześniejszym symbolem wskazanym przez poziom i wybierz tak lub nie.',
        'stroop-colors': 'Przeczytaj słowo, zignoruj kolor tekstu i dotknij wskazanego koloru.',
        'go-no-go': 'Naciśnij tylko przy asie lub królu; przy pozostałych kartach nic nie rób.',
        'trail-making': 'Zacznij od 1 i łącz na zmianę liczbę oraz literę, nie gubiąc ścieżki.',
    },
    tr: {
        memory: 'Öğeleri inceleyin, görünenleri hatırlayın ve eşleşen yanıtı seçin.',
        attention: 'Hedefe odaklanın, dikkat dağıtıcıları yok sayın ve doğru yanıt verin.',
        speed: 'Hedefleri doğru sırayla olabildiğince hızlı ve doğru bulun.',
        logic: 'Kuralı belirleyin ve kurala uyan yanıtı seçin.',
        numbers: 'Sayıları veya miktarları inceleyip doğru sonucu seçin.',
        spatial: 'Şekli veya rotayı zihninizde izleyip doğru sonucu seçin.',
        audio: 'Dikkatle dinleyin, sırayı hatırlayın ve tekrarlayın.',
        planning: 'Hamleleri planlayıp hedefe mümkün olan en az adımla ulaşın.',
        coordination: 'İki kontrolü de kullanın ve işaretçileri kendi şeritlerinde tutun.',
        'symbol-n-back': 'Her sembolü seviyenin belirttiği önceki sembolle karşılaştırıp evet veya hayır seçin.',
        'stroop-colors': 'Kelimeyi okuyun, yazı rengini yok sayın ve kelimenin belirttiği renge dokunun.',
        'go-no-go': 'Yalnızca As veya Papaz çıkınca basın; diğer kartlarda hiçbir şey yapmayın.',
        'trail-making': '1’den başlayın ve yolu kaybetmeden sayı ile harfi sırayla bağlayın.',
    },
    'zh-hans': {
        memory: '仔细观察并记住出现的内容，然后选择匹配的答案。',
        attention: '专注目标，忽略干扰，并准确作答。',
        speed: '按正确顺序尽可能快速、准确地找到目标。',
        logic: '找出规则，然后选择符合规则的答案。',
        numbers: '观察数字或数量，然后选择正确结果。',
        spatial: '在脑中跟随形状或路线，然后选择正确结果。',
        audio: '仔细聆听，记住顺序，然后按顺序重复。',
        planning: '规划移动，并用尽可能少的步骤达到目标。',
        coordination: '同时使用两个控制器，让两个标记保持在各自轨道内。',
        'symbol-n-back': '将每个符号与当前等级所指定的较早符号比较，然后选择“是”或“否”。',
        'stroop-colors': '阅读文字，忽略文字本身的颜色，然后点击文字所表示的颜色。',
        'go-no-go': '仅在出现 A 或 K 时按下；其他牌出现时不要操作。',
        'trail-making': '从 1 开始，按数字和字母交替连接，不要中断路线。',
    },
    'zh-hant': {
        memory: '仔細觀察並記住出現的內容，然後選擇相符的答案。',
        attention: '專注目標、忽略干擾，並準確作答。',
        speed: '依正確順序盡可能快速、準確地找到目標。',
        logic: '找出規則，然後選擇符合規則的答案。',
        numbers: '觀察數字或數量，然後選擇正確結果。',
        spatial: '在腦中跟隨形狀或路線，然後選擇正確結果。',
        audio: '仔細聆聽、記住順序，然後依序重複。',
        planning: '規劃移動，並用盡可能少的步驟達成目標。',
        coordination: '同時使用兩個控制器，讓兩個標記保持在各自軌道內。',
        'symbol-n-back': '將每個符號與目前等級所指定的較早符號比較，然後選擇「是」或「否」。',
        'stroop-colors': '閱讀文字、忽略文字本身的顏色，然後點選文字所表示的顏色。',
        'go-no-go': '只有出現 A 或 K 時才按下；其他牌出現時不要操作。',
        'trail-making': '從 1 開始，依數字和字母交替連接，不要中斷路線。',
    },
    ru: {
        memory: 'Рассмотрите элементы, запомните их и выберите подходящий ответ.',
        attention: 'Сосредоточьтесь на цели, игнорируйте помехи и отвечайте точно.',
        speed: 'Находите цели по порядку как можно быстрее и точнее.',
        logic: 'Определите правило и выберите ответ, который ему соответствует.',
        numbers: 'Изучите числа или количества и выберите правильный результат.',
        spatial: 'Проследите форму или маршрут мысленно и выберите правильный результат.',
        audio: 'Слушайте внимательно, запомните порядок и повторите его.',
        planning: 'Планируйте ходы и достигните цели за минимальное число шагов.',
        coordination: 'Используйте оба элемента управления и удерживайте маркеры на дорожках.',
        'symbol-n-back': 'Сравнивайте каждый символ с более ранним символом, который задаёт ваш уровень, и выбирайте «да» или «нет».',
        'stroop-colors': 'Прочитайте слово, игнорируйте цвет текста и нажмите названный цвет.',
        'go-no-go': 'Нажимайте только при появлении туза или короля; для остальных карт ничего не делайте.',
        'trail-making': 'Начните с 1 и чередуйте число с буквой, не теряя путь.',
    },
    id: {
        memory: 'Amati item, ingat apa yang muncul, lalu pilih jawaban yang cocok.',
        attention: 'Fokus pada target, abaikan gangguan, dan jawab dengan tepat.',
        speed: 'Temukan target secara berurutan secepat dan setepat mungkin.',
        logic: 'Temukan aturannya, lalu pilih jawaban yang mengikuti aturan itu.',
        numbers: 'Amati angka atau jumlah, lalu pilih hasil yang benar.',
        spatial: 'Ikuti bentuk atau rute dalam pikiran, lalu pilih hasil yang benar.',
        audio: 'Dengarkan baik-baik, ingat urutannya, lalu ulangi.',
        planning: 'Rencanakan langkah dan capai tujuan dengan langkah sesedikit mungkin.',
        coordination: 'Gunakan kedua kontrol dan pertahankan kedua penanda di jalurnya.',
        'symbol-n-back': 'Bandingkan setiap simbol dengan simbol sebelumnya sesuai level, lalu pilih ya atau tidak.',
        'stroop-colors': 'Baca katanya, abaikan warna tintanya, lalu ketuk warna yang disebutkan.',
        'go-no-go': 'Tekan hanya saat As atau Raja muncul; jangan lakukan apa pun untuk kartu lain.',
        'trail-making': 'Mulai dari 1, lalu selang-seling angka dan huruf tanpa kehilangan jalur.',
    },
    hi: {
        memory: 'वस्तुओं को ध्यान से देखें, याद रखें और फिर मिलान वाला उत्तर चुनें।',
        attention: 'लक्ष्य पर ध्यान दें, विकर्षणों को अनदेखा करें और सही उत्तर दें।',
        speed: 'लक्ष्यों को सही क्रम में जितनी जल्दी और सही हो सके खोजें।',
        logic: 'नियम पहचानें और उस नियम का पालन करने वाला उत्तर चुनें।',
        numbers: 'संख्याओं या मात्राओं को देखें और सही परिणाम चुनें।',
        spatial: 'आकार या मार्ग को मन में दोहराएँ और सही परिणाम चुनें।',
        audio: 'ध्यान से सुनें, क्रम याद रखें और उसे दोहराएँ।',
        planning: 'चालों की योजना बनाएँ और कम से कम चरणों में लक्ष्य तक पहुँचें।',
        coordination: 'दोनों नियंत्रणों का उपयोग करें और दोनों संकेतकों को उनकी लेन में रखें।',
        'symbol-n-back': 'हर चिह्न की तुलना आपके लेवल द्वारा बताए गए पहले के चिह्न से करें और हाँ या नहीं चुनें।',
        'stroop-colors': 'शब्द पढ़ें, स्याही के रंग को अनदेखा करें और शब्द में बताए रंग पर टैप करें।',
        'go-no-go': 'केवल A या K आने पर दबाएँ; दूसरी कार्ड पर कुछ न करें।',
        'trail-making': '1 से शुरू करें और मार्ग खोए बिना संख्या व अक्षर को बारी-बारी जोड़ें।',
    },
};

export const getLocalizedBrainGameTutorial = (gameId, category, language = 'en') => {
    const normalized = normalizeUFOVLanguage(language);
    const copy = TUTORIAL_COPY[normalized] || TUTORIAL_COPY.en;
    const instruction = copy[gameId] || copy[category] || TUTORIAL_COPY.en[category];

    return instruction ? [instruction] : null;
};
